import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportExportCard = ({ reportData, onExport, onShare }) => {
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportFormat, setExportFormat] = React.useState('pdf');

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      await onExport(format);
      // Simulate export process
      setTimeout(() => {
        setIsExporting(false);
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
    }
  };

  const shareOptions = [
    {
      platform: 'LinkedIn',
      icon: 'Linkedin',
      color: 'text-blue-600',
      action: () => onShare('linkedin')
    },
    {
      platform: 'Email',
      icon: 'Mail',
      color: 'text-gray-600',
      action: () => onShare('email')
    },
    {
      platform: 'Copy Link',
      icon: 'Link',
      color: 'text-primary',
      action: () => onShare('copy')
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Download" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Export & Share Report</h3>
          <p className="text-sm text-text-secondary">Download or share your career analysis</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Export Section */}
        <div>
          <h4 className="font-medium text-text-primary mb-3">Download Report</h4>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="flex bg-muted rounded-lg p-1">
                <button
                  onClick={() => setExportFormat('pdf')}
                  className={`px-3 py-1 rounded text-sm font-medium spring-transition ${
                    exportFormat === 'pdf' ?'bg-surface text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  PDF
                </button>
                <button
                  onClick={() => setExportFormat('docx')}
                  className={`px-3 py-1 rounded text-sm font-medium spring-transition ${
                    exportFormat === 'docx'
                      ? 'bg-surface text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Word
                </button>
              </div>
              <Button
                variant="default"
                onClick={() => handleExport(exportFormat)}
                loading={isExporting}
                iconName="Download"
                iconPosition="left"
                className="flex-1"
              >
                {isExporting ? 'Generating...' : `Download ${exportFormat?.toUpperCase()}`}
              </Button>
            </div>
            <div className="text-xs text-text-secondary">
              Report includes: Career analysis, job recommendations, action plan, and skill assessments
            </div>
          </div>
        </div>

        {/* Share Section */}
        <div>
          <h4 className="font-medium text-text-primary mb-3">Share Report</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {shareOptions?.map((option, index) => (
              <Button
                key={index}
                variant="outline"
                onClick={option?.action}
                className="flex items-center justify-center space-x-2"
              >
                <Icon name={option?.icon} size={16} className={option?.color} />
                <span>{option?.platform}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Report Summary */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="font-medium text-text-primary mb-3">Report Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-secondary">Generated:</span>
              <span className="ml-2 font-medium text-text-primary">
                {new Date()?.toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Job Matches:</span>
              <span className="ml-2 font-medium text-text-primary">
                {reportData?.jobRecommendations?.length}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Strengths:</span>
              <span className="ml-2 font-medium text-text-primary">
                {reportData?.strengths?.length}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Action Items:</span>
              <span className="ml-2 font-medium text-text-primary">
                {reportData?.actionPlan?.phases?.reduce((total, phase) => total + phase?.items?.length, 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Professional Branding Note */}
        <div className="flex items-start space-x-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-text-primary font-medium mb-1">Professional Report</p>
            <p className="text-text-secondary">
              Your exported report includes APLINI branding and professional formatting suitable for sharing with employers or career counselors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportExportCard;